import { useState, useEffect } from 'react';
import { Listing } from '../types';
import { fetchSoldListings, fetchActiveListings } from '../services/idxBroker';
import { mockListings } from '../data/mockListings';

interface UseListingsOptions {
  type: 'sold' | 'active' | 'all';
  useMockFallback?: boolean;
}

interface UseListingsReturn {
  listings: Listing[];
  isLoading: boolean;
  error: string | null;
  isUsingMockData: boolean;
}

export function useListings(options: UseListingsOptions): UseListingsReturn {
  const { type, useMockFallback = true } = options;
  const [listings, setListings] = useState<Listing[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUsingMockData, setIsUsingMockData] = useState(false);

  useEffect(() => {
    async function loadListings() {
      setIsLoading(true);
      setError(null);

      try {
        let fetchedListings: Listing[] = [];

        if (type === 'sold') {
          fetchedListings = await fetchSoldListings();
        } else if (type === 'active') {
          fetchedListings = await fetchActiveListings();
        } else {
          const [sold, active] = await Promise.all([
            fetchSoldListings(),
            fetchActiveListings()
          ]);
          fetchedListings = [...active, ...sold];
        }

        if (fetchedListings.length > 0) {
          setListings(fetchedListings);
          setIsUsingMockData(false);
        } else if (useMockFallback) {
          const filtered = type === 'active'
            ? mockListings.filter(l => l.status === 'active')
            : type === 'sold'
            ? mockListings.filter(l => l.status === 'sold')
            : mockListings;
          setListings(filtered);
          setIsUsingMockData(true);
        }
      } catch (err) {
        setError('Failed to load listings');
        if (useMockFallback) {
          setListings(mockListings);
          setIsUsingMockData(true);
        }
      } finally {
        setIsLoading(false);
      }
    }

    loadListings();
  }, [type, useMockFallback]);

  return { listings, isLoading, error, isUsingMockData };
}

export default useListings;
